import { useEffect, useRef } from 'react';
import { createOtherSaleProduct } from '../../api/otherSales';
import type { CreateOtherSaleProductInput, OtherSaleProduct } from '../../types/otherSales';
import { describeCreateFailure, type CreateFailure } from '../../utils/newProduct';

/**
 * El alta de un producto: la única llamada a `createOtherSaleProduct` de la app. La usan «Nuevo producto»
 * (Bloque E) y su variante mínima, el alta rápida del registro guiado (ONB-001, unificada en ONB-004), para
 * que las dos creen igual:
 *   · Una sola alta a la vez. La guarda es una referencia, no solo el estado: dos toques en el mismo
 *     instante no mandan dos. Ya creado, no se manda otra.
 *   · Si la pantalla se va antes de la respuesta, la respuesta no hace nada (ni éxito ni error).
 *   · Sin red o con un error del servidor no se sabe si se creó; se recuerda, para que un nombre repetido
 *     en el siguiente intento se explique como «probablemente sí se creó» (`describeCreateFailure`).
 *
 * Quien la usa decide qué se ve: recibe los avisos en el mismo orden en que el asistente los manejaba.
 */
export function useProductCreate() {
  const savingRef = useRef(false);
  const createdRef = useRef(false);
  const uncertainRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const create = async (payload: CreateOtherSaleProductInput, fallback: string, on: {
    /** Se va a mandar (ya con la guarda puesta). */
    start: () => void;
    created: (product: OtherSaleProduct) => void;
    failed: (failure: CreateFailure) => void;
    /** Terminó, con éxito o sin él, y la pantalla sigue ahí. */
    settled: () => void;
  }) => {
    if (savingRef.current || createdRef.current) return;
    savingRef.current = true;
    on.start();
    try {
      const { product } = await createOtherSaleProduct(payload);
      if (!aliveRef.current) return;
      createdRef.current = true;
      on.created(product);
    } catch (error) {
      if (!aliveRef.current) return;
      const failure = describeCreateFailure(error, fallback, uncertainRef.current);
      if (failure.uncertain) uncertainRef.current = true;
      on.failed(failure);
    } finally {
      savingRef.current = false;
      if (aliveRef.current) on.settled();
    }
  };

  return { create, savingRef, createdRef };
}
